import { useState, useRef, useEffect } from 'react';
import { usePost } from './API';
import { PublishStep } from '@aidkitorg/types/lib/distroFileNames';
import { PUBLISH_PROGRESS_POLLING_INTERVAL_MS } from './PublishProgressModal';

export function usePublishProgress() {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<PublishStep | null>(null);
  const [done, setDoneState] = useState(false);
  const [error, setErrorState] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const getPublishProgress = usePost('/distro-review/publish-progress');

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = (id: string) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setCurrentStep(null);
    setDoneState(false);
    setErrorState(null);
    setOpen(true);
    pollingRef.current = setInterval(async () => {
      try {
        const progress = await getPublishProgress({ id });
        if (!('error' in progress) && !!progress.stepLabel && (Object.values(PublishStep) as string[]).includes(progress.stepLabel)) {
          setCurrentStep(progress.stepLabel as PublishStep);
        }
      } catch {
        // Polling errors are non-fatal
      }
    }, PUBLISH_PROGRESS_POLLING_INTERVAL_MS);
  };

  const setDone = () => { stopPolling(); setDoneState(true); };
  const setError = (msg: string) => { stopPolling(); setErrorState(msg); };
  const hide = () => { stopPolling(); setOpen(false); };

  return {
    open,
    currentStep,
    done,
    error,
    startPolling,
    stopPolling,
    setDone,
    setError,
    hide,
    onClose: () => setOpen(false),
  };
}
